import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LanguageService } from './language.service';

export type DocumentPersonType =
  | 'fisica_mexicana'
  | 'extranjera'
  | 'moral_fideicomiso'
  | 'entidades_sistema_financiero'
  | 'dependencias_sociedades';

export type DocumentItemIcon = 'person' | 'attachment';

export interface DocumentItem {
  key: string;
  text: string;
  icon: DocumentItemIcon;
}

export interface DocumentGroup {
  personType: DocumentPersonType;
  items: DocumentItem[];
}

export interface DocumentRange {
  id: number;
  minUsd: number;
  maxUsd: number;
  instructions: string;
  note: string | null;
  groups: DocumentGroup[];
}

export interface DocumentMatrix {
  locale: string;
  ranges: DocumentRange[];
}

@Injectable({
  providedIn: 'root',
})
export class DocumentRequirementsService {
  private readonly matrix$: Observable<DocumentMatrix | null>;

  constructor(
    private readonly http: HttpClient,
    private readonly languageService: LanguageService,
  ) {
    // Refetch on every language change; Accept-Language is added by the
    // AcceptLanguageInterceptor. One cached payload serves any amount.
    this.matrix$ = this.languageService.currentLanguage$.pipe(
      switchMap(() => this.fetchMatrix()),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getMatrixCached(): Observable<DocumentMatrix | null> {
    return this.matrix$;
  }

  private fetchMatrix(): Observable<DocumentMatrix | null> {
    return this.http
      .get<{ data?: DocumentMatrix }>(
        `${environment.BASE_URL}/document-requirements/public/matrix`,
      )
      .pipe(
        map((response) => this.normalize(response?.data)),
        catchError(() => of(null)),
      );
  }

  private normalize(data: unknown): DocumentMatrix | null {
    const raw = data as Partial<DocumentMatrix> | undefined;
    if (!raw || !Array.isArray(raw.ranges)) {
      return null;
    }
    return {
      locale: String(raw.locale ?? ''),
      ranges: raw.ranges.map((r) => ({
        id: r.id,
        minUsd: Number(r.minUsd),
        maxUsd: Number(r.maxUsd),
        instructions: r.instructions ?? '',
        note: r.note ?? null,
        groups: Array.isArray(r.groups) ? r.groups : [],
      })),
    };
  }
}
